import type { FC } from 'react'
import { useState } from 'react'

import { Dropzone, MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import FileDocumentOutlineIcon from 'mdi-react/FileDocumentOutlineIcon'
import { useSelector } from 'react-redux'

import { API_ROUTE_DOCUMENT_HPE_CREDIT_CHECK } from '@/api/apiRoutes'
import documentResources from '@/api/document'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { Translation } from '@/components/i18n/Translation'
import { DOCUMENT_TYPES } from '@/components/util/dropzone/constants'
import useAuthFilesDropzone from '@/components/util/dropzone/useAuthFilesDropzone'
import { getCustomerId } from '@/ducks/shopOrderReducer'
import { post } from '@/util/auth'
import { CREDIT_CHECK_STATUS, getCreditCheckTextId } from '@/util/credit-check'

import CreditCheckStatusIcon from './CreditCheckStatusIcon'

// @ts-ignore
const CreditCheckResultDocNeeded = ({ docDetails = [], documents = [], id, loadCreditCheck }) => {
  const customerId = useSelector(getCustomerId)

  // @ts-ignore
  const onUploaded = async (documentId, hpeCreditAppDocID) => {
    const payload = {
      creditCheckId: id,
      documentId,
      hpeCreditAppDocID,
    }

    await post(API_ROUTE_DOCUMENT_HPE_CREDIT_CHECK, payload)
    await loadCreditCheck()
  }

  return (
    <div className="credit-check-result">
      <CreditCheckStatusIcon status={CREDIT_CHECK_STATUS.DOC_REQUIRED} />

      <h2>
        <Translation id={getCreditCheckTextId(CREDIT_CHECK_STATUS.DOC_REQUIRED)} />
      </h2>

      <ul>
        {docDetails?.map((requirement, idx) => {
          const { CreditAppDocDescription, CreditAppDocComments, CreditAppDocID } = requirement
          const detailDocuments = documents.filter(
            // @ts-ignore
            doc => doc.hpe_credit_app_doc_id === parseInt(CreditAppDocID)
          )

          return (
            <li key={idx}>
              <h4>{CreditAppDocDescription}</h4>

              <p>{CreditAppDocComments}</p>

              {detailDocuments.length > 0 && (
                <ul className="uploaded-documents">
                  {detailDocuments.map(doc => (
                    // @ts-ignore
                    <UploadedDocument key={doc.id} {...doc} />
                  ))}
                </ul>
              )}

              <DocDropzone
                creditAppDocID={CreditAppDocID}
                customerId={customerId}
                onUploaded={onUploaded}
              />
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default CreditCheckResultDocNeeded

const DocDropzone: FC<{
  creditAppDocID: string
  customerId: number
  onUploaded: (documentId: unknown, hpeCreditAppDocID: unknown) => Promise<void>
}> = ({ creditAppDocID, customerId, onUploaded }) => {
  const { whiteLabel } = useAppContext()
  const [uploadError, setUploadError] = useState('')

  const { authorizeUpload, isAuthorizingUpload } = useAuthFilesDropzone({
    customerId,
    type: DOCUMENT_TYPES.CREDIT_CHECK_DOCUMENTATION,
    onUpload: res => {
      setUploadError('')
      onUploaded(res.id, Number(creditAppDocID))
    },
    onError: setUploadError,
  })

  return (
    // todo: TEST when this feature is in use again
    <Dropzone
      id="dropzone"
      customError={uploadError}
      type={DOCUMENT_TYPES.ONBOARDING_AUTH_FORM}
      activeDragText={<Translation id="Release file here to upload" />}
      inActiveDragText={<Translation id="Drag file here or click to browse" />}
      isUploading={isAuthorizingUpload}
      onDrop={authorizeUpload}
      whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
    />
  )
}

const UploadedDocument: FC<{ id: number; filename: string }> = ({ id, filename }) => {
  const { whiteLabel } = useAppContext()
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false)

  // todo: TEST when this feature is in use again
  const { refetch: authorizeFileDownload, isInitialLoading: isDownloading } = useQuery(
    queries.documentDownloadAuthorization(id),
    () => documentResources.authorizeFileDownload(id),
    {
      enabled: false,
    }
  )

  return (
    <>
      <li>
        <Button
          isLoading={isDownloading}
          onClick={() => authorizeFileDownload}
          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
        >
          <FileDocumentOutlineIcon />

          {filename}
        </Button>
      </li>

      <MessagePopup
        title={<Translation id="File download error" />}
        message={
          <Translation id="We encountered a problem while downloading your file. Please refresh and try again." />
        }
        isOpen={isErrorPopupOpen}
        onClose={() => setIsErrorPopupOpen(false)}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      />
    </>
  )
}
